import { InstallVideo } from "@/components/site/InstallVideo";
import { SwipeRow } from "@/components/site/SwipeRow";
import { BracketIcon, PlateArrowIcon, WipeIcon } from "@/components/site/GuideIcons";
import { GUIDE_RECOMMENDATIONS } from "@/content/guide";
import { useSeo } from "@/lib/site";

const CARDS = [
  { n: "01", title: "Prepara la pared", text: "Elige una superficie lisa, limpia y seca.", Icon: WipeIcon },
  { n: "02", title: "Fija los soportes", text: "Marca su posición y sigue las indicaciones del adhesivo.", Icon: BracketIcon },
  { n: "03", title: "Coloca tu LUMINA", text: "Acerca la lámina y comprueba que quede firme y nivelada.", Icon: PlateArrowIcon },
];

const SYSTEM_TEXT = [
  "Nuestras láminas cuentan con un soporte trasero de buen grosor que las mantiene separadas de la pared, creando un elegante efecto flotante.",
  "Esta separación aporta profundidad y genera una sombra natural que realza cada fotografía.",
  "El sistema magnético permite retirar la lámina e intercambiarla por otra compatible, conservando los soportes en su lugar.",
];

const PHOTOS = [
  { key: "front", src: "/productos/sistema-frontal.webp", alt: "Lámina LUMINA vista de frente, flotando sobre la pared", label: "Vista frontal" },
  { key: "side", src: "/productos/sistema-lateral.webp", alt: "Lámina LUMINA vista de perfil con el soporte magnético trasero", label: "Vista lateral" },
];

const GuideCard = ({ c }) => (
  <article data-testid={`guide-card-${c.n}`} className="flex h-full items-start gap-4 rounded-xl bg-[#F3EFE8] p-5 sm:p-6">
    <div className="flex shrink-0 flex-col items-start gap-2">
      <span className="font-serif-display text-3xl leading-none text-[#B18A4B]">{c.n}</span>
      <c.Icon />
    </div>
    <div>
      <h3 className="font-serif-display text-xl font-medium">{c.title}</h3>
      <p className="mt-1.5 text-sm leading-relaxed text-[#080808]/75">{c.text}</p>
    </div>
  </article>
);

export default function HowItWorksPage() {
  useSeo({
    title: "Cómo funciona y guía de instalación | LUMINA STUDIO",
    description: "Instala tu lámina magnética flotante LUMINA en minutos: video, tres pasos y el sistema de soporte trasero.",
  });
  return (
    <div className="mx-auto max-w-[1200px] px-5 py-12 sm:px-8 sm:py-16">
      <section id="guia" data-testid="install-guide" aria-labelledby="guia-title" className="scroll-mt-28">
        <div className="grid items-center gap-8 md:grid-cols-[1fr_auto] md:gap-12">
          <div>
            <div className="h-[2px] w-10 bg-[#B18A4B]" />
            <p className="mt-3 font-serif-display text-xl text-[#B18A4B]">Guía de instalación</p>
            <h1 id="guia-title" className="mt-2 font-serif-display text-[34px] font-medium leading-[1.05] sm:text-[52px]">Instala tu LUMINA en minutos</h1>
            <p className="mt-5 text-lg leading-relaxed text-[#080808]/85">Un sistema magnético que sostiene tu lámina y crea un efecto flotante.</p>
            <p className="mt-4 text-base text-[#080808]/70">Mira el video y sigue estas indicaciones.</p>
          </div>
          <InstallVideo testId="install-video" className="justify-self-center" />
        </div>
        <div className="mt-10">
          <SwipeRow testId="guide-row" label="Pasos de instalación" items={CARDS} getKey={(c) => c.n} render={(c) => <GuideCard c={c} />} />
        </div>
      </section>

      <section data-testid="system-section" aria-labelledby="sistema" className="mt-12 border-t border-[#080808]/10 pt-12">
        <div className="grid items-center gap-8 md:grid-cols-[1fr_2fr] md:gap-10">
          <div>
            <div className="h-[2px] w-10 bg-[#B18A4B]" />
            <h2 id="sistema" className="mt-3 font-serif-display text-[30px] font-medium sm:text-[40px]">El sistema LUMINA</h2>
            <div data-testid="system-text" className="mt-4 space-y-3 text-base leading-[1.7] text-[#080808]/80">{SYSTEM_TEXT.map((t) => <p key={t}>{t}</p>)}</div>
          </div>
          <SwipeRow testId="system-row" cols="md:grid-cols-2" label="Fotos del sistema LUMINA" items={PHOTOS} getKey={(p) => p.key}
            render={(p) => (
              <figure className="m-0">
                <img data-testid={`system-photo-${p.key}`} src={p.src} alt={p.alt} loading="lazy" className="aspect-[3/4] w-full rounded-xl object-cover" />
                <figcaption className="mt-2 text-sm text-[#080808]/65">{p.label}</figcaption>
              </figure>
            )} />
        </div>
      </section>

      <section data-testid="guide-recommendations" className="mt-12 rounded-xl border border-[#080808]/10 p-6 sm:p-8">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em]">Recomendaciones importantes</h2>
        <ul className="mt-4 list-disc space-y-1.5 pl-5 text-sm leading-[1.65] text-[#080808]/80">
          {GUIDE_RECOMMENDATIONS.map((r) => <li key={r}>{r}</li>)}
        </ul>
      </section>
    </div>
  );
}
